import { useEffect, useRef } from 'react';

import { GetChatHistory } from '@/api/fe-api/view/queries/ai/get-chat-history';
import { useFeatureFlag } from '@/hooks/use-feature-flag';

import { AiChatboxDescription } from './ai-chatbox-description';
import { LoadingText } from './loading-text';
import { MessageBot } from './message-bot';
import { MessageUser } from './message-user';
import { chatStyle } from './style';

export type AiChatboxFeedProps = {
  onSubmitPrompt: (prompt: string) => void;
  streamedMessage: string;
  submittedMessage: string;
  isLoading: boolean;
};

export function AiChatboxFeed({
  onSubmitPrompt,
  streamedMessage,
  submittedMessage,
  isLoading,
}: AiChatboxFeedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = GetChatHistory.useQuery();
  const featureFlag = useFeatureFlag();

  const style = chatStyle({
    hasMessages:
      Boolean(submittedMessage) ||
      (data?.chatHistory && data.chatHistory.length > 0),
  });

  function scrollToBottomSmart(force = false) {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

    const threshold = 100; // px, tolerance for being "near" bottom

    if (force || distanceToBottom < threshold) {
      container.scrollTo({
        top: scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  useEffect(() => {
    scrollToBottomSmart(true);
  }, [data?.chatHistory, isLoading]);

  if (!featureFlag('ai')) {
    return null;
  }

  return (
    <div ref={containerRef} className={style.feed()}>
      <AiChatboxDescription onSubmitPrompt={onSubmitPrompt} />
      <div className="space-y-6 p-4">
        {data?.chatHistory?.map((message, index) =>
          message.sender === 'user' ? (
            <MessageUser key={index} message={message.content} />
          ) : (
            <MessageBot key={index} message={message.content} />
          ),
        )}
        <MessageUser message={submittedMessage} />
        <LoadingText
          isLoading={isLoading}
          descriptions={[
            'Thinking...',
            'Processing your request...',
            'Analyzing information...',
            'Generating response...',
          ]}
        />
        <MessageBot message={streamedMessage} />
      </div>
    </div>
  );
}
